import {BehaviorSubject, Observable} from 'rxjs';
import {WorkBook, WorkSheet} from 'xlsx';
import {ExcelHelperUtil} from '../utils/excel-helper-util';

addEventListener('message', ({data}) => {
  let workBook: WorkBook | null = null;

  if (data.objectArray && Array.isArray(data.objectArray) && data.objectArray.length > 0) {
    const workSheet: WorkSheet = ExcelHelperUtil.createWorkSheetFromObjects(data.objectArray);
    workBook = ExcelHelperUtil.createWorkBookFromSheets([workSheet], [data.sheetName]);
  } else if (data.arrayOfArrays && Array.isArray(data.arrayOfArrays) && data.arrayOfArrays.length > 0) {
    const workSheet: WorkSheet = ExcelHelperUtil.createWorkSheetFromArrays(data.arrayOfArrays);
    workBook = ExcelHelperUtil.createWorkBookFromSheets([workSheet], [data.sheetName]);
  }

  postMessage(workBook);
});

export class ExcelUtil {

  /**
   * Exports an Excel Sheet of an object array using a Web Worker for multi-threading.
   * Property names of objects will be used to define column titles in Excel sheet
   *
   * @param objectArray an array of flat objects
   * @param sheetName Excel Sheet Name
   */
  public static exportObjectArrayMultiThreaded(objectArray: any[], sheetName: string): Observable<WorkBook | null> {

    const exportCompleteSubject = new BehaviorSubject<WorkBook | null>(null);

    if (typeof Worker !== 'undefined') {
      const worker = new Worker(new URL('./excel-export.worker.ts', import.meta.url));
      worker.onmessage = ({data}) => {
        this.handleWorkerResponse(data, exportCompleteSubject);
      };
      worker.postMessage({objectArray, sheetName});
      return exportCompleteSubject;
    } else {
      // Fallback creates workbook on main thread
      const workBook = this.singleThreadExportObjectArray(objectArray, sheetName);
      return new BehaviorSubject<WorkBook | null>(workBook).asObservable();
    }
  }

  /**
   * Exports an Excel Sheet of an array of arrays using a Web Worker for multi-threading.
   * The first array must define the column names for the Excel File
   *
   * @param arrayOfArrays an array of flat objects
   * @param sheetName
   */
  public static exportArraysMultiThreaded(arrayOfArrays: any[][], sheetName: string): Observable<WorkBook | null> {

    const exportCompleteSubject = new BehaviorSubject<WorkBook | null>(null);

    if (typeof Worker !== 'undefined') {
      const worker = new Worker(new URL('../web-workers/excel-export.worker.ts', import.meta.url));
      worker.onmessage = ({data}) => {
        this.handleWorkerResponse(data, exportCompleteSubject);
      };
      worker.postMessage({arrayOfArrays, sheetName});
      return exportCompleteSubject;
    } else {
      // Create workbook on main thread as a fall-back
      const workBook = this.singleThreadExportArrays(arrayOfArrays, sheetName);
      return new BehaviorSubject(workBook).asObservable();
    }
  }

  /**
   * Single thread fall-back to export Excel File based on an object array
   *
   * @param objectArray
   * @param sheetName
   * @private
   */
  private static singleThreadExportObjectArray(objectArray: any[], sheetName: string): WorkBook | null {

    if (objectArray && Array.isArray(objectArray) && objectArray.length > 0) {
      const workSheet: WorkSheet = ExcelHelperUtil.createWorkSheetFromObjects(objectArray);
      return ExcelHelperUtil.createWorkBookFromSheets([workSheet], [sheetName]);
    }

    return null;
  }

  /**
   * Single thread fall-back to export Excel File based on an array of arrays
   *
   * @param arrayOfArrays
   * @param sheetName
   * @private
   */
  private static singleThreadExportArrays(arrayOfArrays: any[][], sheetName: string): WorkBook | null {

    if (arrayOfArrays && arrayOfArrays.length > 0) {
      const workSheet: WorkSheet = ExcelHelperUtil.createWorkSheetFromArrays(arrayOfArrays);
      return ExcelHelperUtil.createWorkBookFromSheets([workSheet], [sheetName]);
    }

    return null;
  }

  /**
   * - When the Web-Worker has completed its task, returns the 'next' value via the BehaviourSubject so the calling method
   * can handle any further logic.
   * - Returns an 'error' via the BehaviourSubject if the data is falsy
   *
   *
   * @param data
   * @param exportCompleteSubject
   * @private
   */
  private static handleWorkerResponse(data: any, exportCompleteSubject: BehaviorSubject<WorkBook | null>) {
    if (data) {
      exportCompleteSubject.next(data);
    } else {
      exportCompleteSubject.error(null);
    }
  }
}
